import qs from "qs";
// src/internal.ts
const KeboolaHttpHeader = {
	STORAGE_API_TOKEN: "X-StorageApi-Token",
	MANAGEMENT_API_TOKEN: "X-Kbc-Manageapitoken",
	STORAGE_API_URL: "x-storageapi-url",
	PROJECT_ID: "X-KBC-ProjectId",
	UI_VERSION: "X-KBC-Ui-Version",
	/** Storage job a request enqueued, reported on the response rather than in its body. */
	STORAGE_API_JOB_ID: "X-StorageApi-Job-Id",
	/** Set to "1" to opt an auth call into browser/cookie mode. */
	CSRF: "X-KBC-CSRF"
};
/** End-user identity apps-proxy hands a data app, forwarded unchanged to Query Service. */
const APP_USER_TOKEN_HEADER = "X-Kbc-App-User-Token";
/** Query Service's `exception` for a query that row-level security refused. */
const ROW_LEVEL_SECURITY_DENIED_MESSAGE = "Row-level security does not allow this query";
/** Request headers that carry a credential — never kept on an error or written to a log. */
const CREDENTIAL_HEADERS = [
	KeboolaHttpHeader.STORAGE_API_TOKEN,
	KeboolaHttpHeader.MANAGEMENT_API_TOKEN,
	APP_USER_TOKEN_HEADER,
	"authorization",
	"cookie"
];
const REDACTED = "[REDACTED]";
/** The headers as a record, each one named in `redacted` (any case) set to `[REDACTED]`. */
const redactHeaders = (headers, redacted) => {
	const redactedSet = new Set(redacted.map((name) => name.toLowerCase()));
	const result = {};
	headers.forEach((value, name) => {
		result[name] = redactedSet.has(name.toLowerCase()) ? REDACTED : value;
	});
	return result;
};
const HttpStatus = {
	NO_CONTENT: 204,
	BAD_REQUEST: 400,
	UNAUTHORIZED: 401,
	FORBIDDEN: 403,
	NOT_FOUND: 404,
	UNPROCESSABLE_ENTITY: 422,
	INTERNAL_SERVER_ERROR: 500,
	BAD_GATEWAY: 502,
	SERVICE_UNAVAILABLE: 503,
	GATEWAY_TIMEOUT: 504
};
const ENCRYPTED_VALUE_PREFIX = "KBC::";
// src/errors/ApiError.ts
/**
* The status line, as a message an error reporter can group on.
*
* - `statusText` is empty on every HTTP/2 response: the protocol dropped the reason phrase, and
*   `fetch` reports that as `''` rather than synthesising one.
* - So the status number leads, and `statusText` only decorates it where a server sent one.
* - Without this an HTTP/2 failure reaches Sentry as `Error:` with an empty message, and the
*   status — the one field that says what went wrong — is absent from the report.
*/
const statusLine = (response) => response.statusText ? `${response.status} ${response.statusText}` : `${response.status}`;
/**
* A body-less copy of the request with every credential header masked.
*
* - Keeps the method, URL and the remaining headers for whoever handles the error.
* - A masked header still appears, as `[REDACTED]`, so a reader sees it was sent.
* - A copy, not an edit: the original request may still be retried with its token.
*/
const withoutCredentials = (request) => new Request(request.url, {
	method: request.method,
	headers: redactHeaders(request.headers, CREDENTIAL_HEADERS)
});
var ApiError = class extends Error {
	response;
	request;
	data;
	constructor({ response, request, data }) {
		super(statusLine(response));
		this.name = "ApiError";
		this.response = response;
		this.request = withoutCredentials(request);
		this.data = data;
	}
};
// src/fetchClient/createFetchClient/utils.ts
/**
* Strips trailing slashes from a base URL so concatenating a leading-slash
* path (e.g. `/v2/storage`) never produces a double slash.
*
* Without this, callers that pass `https://connection.keboola.com/` end up
* issuing requests to `https://connection.keboola.com//v2/storage/...`, which
* the Storage API rejects with a 404.
*/
const normalizeBaseUrl = (baseUrl) => baseUrl.replace(/\/+$/, "");
const HttpHeader = {
	CONTENT_TYPE: "content-type",
	CONTENT_DISPOSITION: "content-disposition",
	AUTHORIZATION: "Authorization",
	IDEMPOTENCY_KEY: "Idempotency-Key",
	IF_MATCH: "If-Match"
};
const HttpContentType = {
	JSON: "application/json",
	TEXT_PLAIN: "text/plain",
	FORM_DATA: "multipart/form-data",
	TEXT_HTML: "text/html"
};
const defaultValidateStatus = ({ response }) => response.status >= 200 && response.status <= 299;
/**
* Whether a response declares HTML.
*
* - Media types are case-insensitive, so `Text/HTML` counts.
* - A header fact only: a `text/html` response may still carry no body at all.
*/
const isHtmlResponse = ({ response }) => !!response.headers.get(HttpHeader.CONTENT_TYPE)?.toLowerCase().includes(HttpContentType.TEXT_HTML);
/**
* Whether a response carried no payload, across every shape `parseData` produces for one:
*
* 1. `null` — a `204`, or a JSON body that was empty or whitespace.
* 2. `''` — any other media type with an empty body, read as text.
*
* Whitespace-only text counts as empty, matching how `parseJson` already treats it.
*/
const isContentEmpty = ({ data }) => data === null || data === void 0 || typeof data === "string" && data.trim() === "";
function removeUndefined(obj) {
	const objCopy = { ...obj };
	for (const [key, value] of Object.entries(objCopy)) if (value == null) delete objCopy[key];
	return objCopy;
}
const JSON_MEDIA_TYPE = /^application\/([\w.-]+\+)?json$/;
const getMediaType = (response) => response.headers.get(HttpHeader.CONTENT_TYPE)?.split(";")[0]?.trim().toLowerCase() ?? "";
const parseJson = async (response) => {
	const text = await response.text();
	return text.trim() === "" ? null : JSON.parse(text);
};
const parseData = async (response, parseAs = "auto") => {
	if (response.status === HttpStatus.NO_CONTENT) return null;
	if (parseAs === "text") return response.text();
	if (parseAs === "blob") return response.blob();
	if (parseAs === "json") return parseJson(response);
	const mediaType = getMediaType(response);
	if (JSON_MEDIA_TYPE.test(mediaType)) return parseJson(response);
	if (mediaType === "application/octet-stream") return response.blob();
	return response.text();
};
const cleanHeadersInit = (headersInit) => {
	if (Array.isArray(headersInit)) return headersInit;
	if (headersInit instanceof Headers) return headersInit;
	if (headersInit == null) return headersInit;
	return removeUndefined(headersInit);
};
const createHeaders = (headersInitA, headersInitB) => {
	const headersA = new Headers(cleanHeadersInit(headersInitA));
	new Headers(cleanHeadersInit(headersInitB)).forEach((value, key) => {
		headersA.set(key, value);
	});
	return headersA;
};
const createPath = (path, pathParam = {}, encodePathParam = encodeURIComponent) => path.replace(/\{([^}]+)}/g, (_, key) => {
	if (!(key in pathParam)) throw new Error(`Path parameter "${key}" is missing in the path "${path}"`);
	return encodePathParam(String(pathParam[key]));
});
const createSearch = (query, options = {}) => {
	return qs.stringify(query, {
		encodeValuesOnly: true,
		skipNulls: true,
		...options
	});
};
const createBody = (body, headers) => {
	if (body == null) return null;
	if (body instanceof FormData) return body;
	if (typeof body === "string") return body;
	const stringifyBody = JSON.stringify(body);
	const stringBody = stringifyBody === "{}" ? null : stringifyBody;
	if (stringBody) headers.set(HttpHeader.CONTENT_TYPE, HttpContentType.JSON);
	return stringBody;
};
const parseContentDispositionHeader = (contentDisposition) => {
	return {
		type: (() => {
			return (contentDisposition?.match(/^\s*(inline|attachment)\s*;/i))?.[1]?.toLowerCase() ?? "unknown";
		})(),
		filename: (() => {
			const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/i);
			if (filenameMatch) return filenameMatch[1];
			const encodedFilenameMatch = contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
			if (encodedFilenameMatch?.[1]) return decodeURIComponent(encodedFilenameMatch[1]);
			return (contentDisposition?.match(/filename=([^;]+)/i))?.[1] ?? null;
		})()
	};
};
const createFetchRequest = ({ url, method, params, options = {}, defaultOptions }) => {
	const { baseUrl, validateStatus: defValidateStatus = defaultValidateStatus, headers: defaultHeaders, fetchFn: defaultFetchFn, encodePathParam, ...restDefaultOptions } = defaultOptions;
	const { validateStatus, queryArrayFormat, headers: endpointHeaders, fetchFn, parseAs, ...restOptions } = options;
	const headers = createHeaders(defaultHeaders, endpointHeaders);
	const path = createPath(url, params.path, encodePathParam);
	const search = createSearch(params.query ?? {}, { arrayFormat: queryArrayFormat });
	const body = createBody(params.body, headers);
	const urlInstance = new URL(normalizeBaseUrl(baseUrl) + path);
	urlInstance.search = search;
	return {
		request: new Request(urlInstance, {
			...restDefaultOptions,
			...restOptions,
			headers,
			method: method.toUpperCase(),
			body
		}),
		validateStatus: validateStatus ?? defValidateStatus,
		fetchFn: fetchFn ?? defaultFetchFn,
		parseAs
	};
};
// src/fetchClient/createFetchClient/createFetchClient.ts
const isApiError = (error) => error instanceof ApiError;
const isAbortError = (error) => error instanceof DOMException && error.name === "AbortError";
const isTimeoutError = (error) => error instanceof DOMException && error.name === "TimeoutError";
const isFetchClientError = (error) => error instanceof TypeError || error instanceof SyntaxError || isApiError(error);
const coreFetch = async ({ request, validateStatus, fetchFn = fetch, parseAs }) => {
	const response = await fetchFn(request);
	const apiResponse = {
		request,
		response,
		data: await parseData(response, parseAs)
	};
	const boolOrError = validateStatus(apiResponse);
	if (isApiError(boolOrError)) throw boolOrError;
	if (!boolOrError) throw new ApiError(apiResponse);
	return apiResponse;
};
const createFetchClient = ({ middlewares = [], baseUrl: _baseUrl, ...defaultOptions }) => {
	const fetchWithMiddlewares = middlewares.reduceRight((next, middleware) => middleware(next), coreFetch);
	const resolveBaseUrl = async () => typeof _baseUrl === "function" ? _baseUrl() : _baseUrl;
	const createFetchMethod = (method) => async (url, params, options = {}) => {
		const baseUrl = await resolveBaseUrl();
		const request = createFetchRequest({
			url,
			method,
			params,
			defaultOptions: {
				...defaultOptions,
				baseUrl
			},
			options
		});
		return (options?.middlewares ?? []).reduceRight((next, middleware) => middleware(next), fetchWithMiddlewares)(request);
	};
	return {
		get: createFetchMethod("get"),
		post: createFetchMethod("post"),
		put: createFetchMethod("put"),
		patch: createFetchMethod("patch"),
		delete: createFetchMethod("delete")
	};
};
export { HttpStatus as _, isTimeoutError as a, ROW_LEVEL_SECURITY_DENIED_MESSAGE as b, defaultValidateStatus as c, normalizeBaseUrl as d, parseContentDispositionHeader as f, ENCRYPTED_VALUE_PREFIX as g, CREDENTIAL_HEADERS as h, isFetchClientError as i, isContentEmpty as l, APP_USER_TOKEN_HEADER as m, isAbortError as n, HttpContentType as o, ApiError as p, isApiError as r, HttpHeader as s, createFetchClient as t, isHtmlResponse as u, KeboolaHttpHeader as v, redactHeaders as x, REDACTED as y };

//# sourceMappingURL=createFetchClient-Dc_ZMuq7.js.map